// Shared chart types & constants used by both the render effect and the
// tooltip builders (kept here to avoid import cycles).

export interface TradeOverlayData {
    trades: Array<{
        /**
         * The trade's own identity where the source has one — in practice the
         * transaction id, which a multi-fill transaction shares. Used to merge
         * the trader overlay with the window's trades, matching the convention
         * mergeTradeSources already dedupes on. It is preferred over tradeKey(),
         * which is field-derived: two genuinely distinct fills that agree on
         * every field collide under it, and merging on it would drop one and
         * under-count its cluster.
         */
        id?: string;
        timestamp: Date;
        price: number;
        side: 'buy' | 'sell';
        outcomeIndex: number | null;
        amount: number;
        quoteAmount: number;
        account: string;
        marketId: string;
    }>;
    highlightedAccounts: Set<string>;
    accountColorMap: Map<string, string>;
}

export interface PositionSnap {
    t: Date;
    pos: number; // net YES position (yesShares - noShares)
    yesShares: number;
    noShares: number;
    yesCostBasis: number; // total USD spent buying YES
    noCostBasis: number; // total USD spent buying NO
    yesBought: number; // total YES tokens bought (for avg cost)
    noBought: number;
    realizedPnl: number; // locked-in PnL from sells
    totalPnl: number;
}

// PreparedSignal: normalised signal ready for rendering (screen coords included)
export interface PreparedSignal {
    id: string;
    conditionId: string;
    openedAt: Date;
    closedAt: Date | null;
    lastSeenAt: Date | null;
    direction: string | null;
    signalType: string;
    traderGroup: string;
    status: string;
    score: number;
    color: string;
    py: number;
    px: number;
    address: string | null;
    addressAlias: string | null;
    factDeltaUsd: number | null;
    factSide: string | null;
    sessionOpeningPrice: number | null;
    sessionPeakPrice: number | null;
    sessionClosePrice: number | null;
    windowMinutes: number | null;
}

export const SIGNAL_TYPE_LABELS: Record<string, string> = {
    position_opened: 'opened',
    position_increased: 'increased',
    position_decreased: 'decreased',
    position_change: 'change',
    probability_spike: 'spike',
    probability_change: 'prob',
    volume_anomaly: 'vol',
};
